import React, { useState, useEffect } from 'react';
import axiosInstance from '../AxiosInstance';
import { jwtDecode } from 'jwt-decode';
import { trackInsightsViewed, trackInsightsNewBadgeClicked } from '../services/analytics';
import { Toast, LoadingSpinner } from './v2-utils';
import '../styles/v2.css';

const CARD_COLORS = [
  { bg: 'linear-gradient(135deg,#1d4ed8,#1e40af)', light: '#eff6ff', text: '#1d4ed8' },
  { bg: 'linear-gradient(135deg,#059669,#065f46)', light: '#f0fdf4', text: '#065f46' },
  { bg: 'linear-gradient(135deg,#7c3aed,#6d28d9)', light: '#f5f3ff', text: '#6d28d9' },
  { bg: 'linear-gradient(135deg,#d97706,#92400e)', light: '#fffbeb', text: '#92400e' },
  { bg: 'linear-gradient(135deg,#0891b2,#0e7490)', light: '#ecfeff', text: '#0e7490' },
];

const ICONS = [
  <svg style={{ width: 22, height: 22, color: 'white' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" /></svg>,
  <svg style={{ width: 22, height: 22, color: 'white' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" /></svg>,
  <svg style={{ width: 22, height: 22, color: 'white' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" /></svg>,
  <svg style={{ width: 22, height: 22, color: 'white' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg>,
  <svg style={{ width: 22, height: 22, color: 'white' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z" /></svg>,
];

function formatDate(d) {
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function InsightsV2() {
  const [insights, setInsights]         = useState(null);
  const [loading, setLoading]           = useState(true);
  const [refreshing, setRefreshing]     = useState(false);
  const [hasNew, setHasNew]             = useState(false);
  const [lastViewed, setLastViewed]     = useState(null);
  const [userId, setUserId]             = useState(null);
  const [error, setError]               = useState(null);
  const [toast, setToast]               = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setError('No authentication token found.'); setLoading(false); return; }
    try {
      const decoded = jwtDecode(token);
      setUserId(decoded.id || decoded.userId || decoded.user_id);
    } catch { setError('Authentication error. Please log in again.'); setLoading(false); }
  }, []);

  useEffect(() => {
    if (userId) { fetchInsights(); checkNew(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchInsights = async (isRefresh = false) => {
    if (!userId) return;
    isRefresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`/insights/${userId}`);
      setInsights(res.data);
      await axiosInstance.post(`/insights/${userId}/mark-viewed`);
      if (isRefresh && hasNew) trackInsightsNewBadgeClicked();
      trackInsightsViewed();
      setHasNew(false);
    } catch { setError('Failed to load insights. Please try again.'); }
    finally { setLoading(false); setRefreshing(false); }
  };

  const checkNew = async () => {
    if (!userId) return;
    try {
      const res = await axiosInstance.get(`/insights/${userId}/check-new`);
      setHasNew(res.data.hasNew);
      if (res.data.lastViewed) setLastViewed(new Date(res.data.lastViewed));
    } catch {}
  };

  const insightsArray = insights
    ? [1, 2, 3, 4, 5].map(i => ({ id: i, text: insights[`insight${i}`], updated_at: insights.last_updated, version: insights.version_number })).filter(x => x.text?.trim())
    : [];

  if (loading) return <LoadingSpinner message="Loading business insights…" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ color: '#0f172a', fontSize: 20, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>Business Insights</h1>
            {hasNew && (
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: '#fef2f2', color: '#b91c1c' }}>New</span>
            )}
          </div>
          {insights && (
            <p style={{ color: '#94a3b8', fontSize: 12, margin: '4px 0 0' }}>
              Version {insights.version_number} · Last updated: {formatDate(insights.last_updated)}
            </p>
          )}
        </div>
        <button onClick={() => fetchInsights(true)} disabled={refreshing}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: 'white', color: '#475569', fontSize: 13, fontWeight: 600, cursor: refreshing ? 'not-allowed' : 'pointer', opacity: refreshing ? 0.6 : 1 }}>
          <svg style={{ width: 15, height: 15, animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
          </svg>
          {refreshing ? 'Refreshing…' : 'Refresh'}
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </button>
      </div>

      {/* New insights banner */}
      {hasNew && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderRadius: 12, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
          <svg style={{ width: 20, height: 20, color: '#3b82f6', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
          </svg>
          <p style={{ margin: 0, fontSize: 13, color: '#1d4ed8', fontWeight: 500 }}>
            New insights available since your last visit{lastViewed && ` on ${formatDate(lastViewed)}`}.
          </p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ padding: '14px 18px', borderRadius: 12, background: '#fef2f2', border: '1px solid #fecaca' }}>
          <p style={{ margin: 0, fontSize: 13, color: '#b91c1c', fontWeight: 500 }}>{error}</p>
        </div>
      )}

      {/* Empty state */}
      {!error && insightsArray.length === 0 && (
        <div className="v2-card" style={{ padding: 48, textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <svg style={{ width: 28, height: 28, color: '#94a3b8' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
            </svg>
          </div>
          <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#1e293b' }}>No insights yet</h3>
          <p style={{ margin: 0, fontSize: 13, color: '#94a3b8', lineHeight: 1.6 }}>Your business insights will appear here once they're generated. Check back later!</p>
        </div>
      )}

      {/* Insight cards */}
      {insightsArray.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {insightsArray.map((insight, i) => {
            const col = CARD_COLORS[i % CARD_COLORS.length];
            return (
              <div key={insight.id} style={{ borderRadius: 16, background: col.bg, padding: 24, display: 'flex', flexDirection: 'column', gap: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {ICONS[i % ICONS.length]}
                  </div>
                  {insight.id === 1 && hasNew && (
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 99, background: 'rgba(255,255,255,0.25)', color: 'white' }}>New</span>
                  )}
                </div>
                <div>
                  <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Business Insight {insight.id} · v{insight.version}
                  </p>
                  <p style={{ margin: 0, fontSize: 14, color: 'white', lineHeight: 1.65 }}>{insight.text}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary row */}
      {insightsArray.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
          {[
            { label: 'Active Insights', value: insightsArray.length, color: '#1d4ed8', bg: '#eff6ff' },
            { label: 'Current Version', value: insights?.version_number || 0, color: '#065f46', bg: '#f0fdf4' },
            { label: 'Updates Available', value: hasNew ? 'Yes' : 'No', color: '#7c3aed', bg: '#f5f3ff' },
          ].map(s => (
            <div key={s.label} className="v2-card" style={{ padding: '18px 20px', textAlign: 'center' }}>
              <p style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 900, color: s.color, letterSpacing: '-0.03em' }}>{s.value}</p>
              <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
